import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconCheck, IconLink, IconMail } from './Icons';

interface Props {
  onDismiss: () => void;
}

const TOTAL_STEPS = 4;
const STORAGE_KEY = 'wizard_step';

export default function OnboardingWizard({ onDismiss }: Props) {
  const navigate = useNavigate();

  const [step, setStep] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? Math.max(1, Math.min(parseInt(saved), TOTAL_STEPS)) : 1;
  });

  const [connectError, setConnectError] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(step));
  }, [step]);

  // When on step 2, check if Gmail is already connected and auto-advance
  useEffect(() => {
    if (step !== 2) return;
    checkGmailAndAdvance();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkGmailAndAdvance() {
    try {
      const res = await fetch('/api/auth/gmail/status');
      if (!res.ok) return;
      const data = await res.json();
      if (data.connected) setStep(3);
    } catch {
      // stay on step 2
    }
  }

  async function handleConnectGmail() {
    setConnectError(false);
    try {
      const res = await fetch('/api/auth/gmail/start');
      if (!res.ok) throw new Error();
      const { authUrl } = await res.json();
      window.location.href = authUrl;
    } catch {
      setConnectError(true);
    }
  }

  async function completeOnboarding() {
    await fetch('/api/onboarding/complete', { method: 'POST' });
    localStorage.removeItem(STORAGE_KEY);
    onDismiss();
    navigate('/app/subscriptions');
  }

  async function skipSetup() {
    await fetch('/api/onboarding/complete', { method: 'POST' });
    localStorage.removeItem(STORAGE_KEY);
    onDismiss();
  }

  return (
    <div className="wizard-overlay" role="dialog" aria-modal="true">
      <div className="wizard-card">

        {step >= 2 && step <= 3 && (
          <button className="wizard-skip" onClick={skipSetup}>
            Skip setup
          </button>
        )}

        <div className="wizard-progress">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(n => (
            <div
              key={n}
              className={`wizard-dot${n === step ? ' wizard-dot--active' : n < step ? ' wizard-dot--done' : ''}`}
            />
          ))}
        </div>

        {/* Step 1 — Welcome */}
        {step === 1 && (
          <div className="wizard-step">
            <div className="wizard-icon wizard-icon--accent">
              <IconMail size={24} />
            </div>
            <h2 className="wizard-title">Let's get your inbox under control</h2>
            <p className="wizard-desc">
              The Email Helper scans your inbox to detect subscriptions, newsletters, and
              mailing lists — then lets you archive, delete, or label them in bulk. Setup takes
              about a minute.
            </p>
            <div className="wizard-actions">
              <button className="btn-primary" onClick={() => setStep(2)}>
                Get started
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Connect Gmail */}
        {step === 2 && (
          <div className="wizard-step">
            <div className="wizard-icon">
              <IconLink size={24} />
            </div>
            <h2 className="wizard-title">Connect your Gmail account</h2>
            <p className="wizard-desc">
              Signing in with Google confirms your identity — it doesn't give The Email Helper
              access to your inbox. Click below to grant the additional inbox permissions needed
              to detect and manage your subscriptions. You can disconnect at any time from Settings.
            </p>
            {connectError && (
              <p className="wizard-error">Could not start the Gmail connection. Please try again.</p>
            )}
            <div className="wizard-actions">
              <button className="btn-primary" onClick={handleConnectGmail}>
                Connect Gmail
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Processing */}
        {step === 3 && (
          <div className="wizard-step wizard-step--centered">
            <div className="wizard-icon wizard-icon--accent">
              <IconMail size={24} />
            </div>
            <h2 className="wizard-title">Gmail connected</h2>
            <p className="wizard-desc">
              We're processing your inbox in the background. This may take a few minutes
              depending on the size of your inbox. Your subscriptions and senders will appear
              automatically as they are detected — you don't need to keep this screen open.
            </p>
            <div className="wizard-actions">
              <button className="btn-primary" onClick={() => setStep(4)}>
                Go to dashboard
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — Done */}
        {step === 4 && (
          <div className="wizard-step wizard-step--centered">
            <div className="wizard-icon wizard-icon--success">
              <IconCheck size={24} />
            </div>
            <h2 className="wizard-title">You're all set</h2>
            <p className="wizard-desc">
              Your inbox is being processed in the background. Come back any time to manage
              subscriptions, archive senders, and more.
            </p>
            <div className="wizard-actions">
              <button className="btn-primary" onClick={completeOnboarding}>
                Go to my inbox
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
